/**
 * Hooks for communications management
 */

import { useState, useEffect, useCallback } from 'react'
import type {
  Communication,
  CreateOutboundRequest,
  RecordInboundRequest,
  ListCommunicationsQuery,
  CommunicationTemplate,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  RenderTemplateRequest,
  RenderedTemplate,
  ListTemplatesQuery,
} from '../types/case-container'
import {
  listCommunications,
  getCommunication,
  createOutbound,
  recordInbound,
  deleteCommunication,
  listTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  renderTemplate,
} from '../services/communications'
import { runMutation } from './use-collection-requests/run-mutation'

// =============================================================================
// useCommunications - List communications for a case
// =============================================================================

interface UseCommunicationsResult {
  communications: Communication[]
  total: number
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch communications for a case
 */
export function useCommunications(
  caseId: string | null,
  params?: ListCommunicationsQuery
): UseCommunicationsResult {
  const [communications, setCommunications] = useState<Communication[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Destructure primitive fields from `params` so the dep array below depends on
  // stable scalar values, not on the object reference. Callers commonly pass a
  // fresh object literal (`{ page, perPage }`) every render — including the
  // object itself in the dep array recreates `fetch` on every render, which
  // in turn re-fires the `useEffect([fetch])` below, triggering an infinite
  // fetch loop that exhausts the browser's HTTP socket pool
  // (net::ERR_INSUFFICIENT_RESOURCES).
  const direction = params?.direction
  const communicationType = params?.communicationType
  const commsLimit = params?.limit
  const commsOffset = params?.offset

  const fetch = useCallback(async () => {
    if (!caseId) {
      setCommunications([])
      setTotal(0)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Pass `params` through (not a reconstructed literal) to preserve the
      // shape's distinction between "absent" and "explicit undefined" under
      // exactOptionalPropertyTypes. Primitive fields are in the dep array
      // below so the closure captures fresh `params` whenever any field
      // actually changes.
      const data = await listCommunications(caseId, params)
      setCommunications(data.communications)
      setTotal(data.total)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch communications')
      setCommunications([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
    // Intentionally omits `params` (unstable object reference); we depend on
    // each primitive field instead. See the comment block above the destructuring.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, direction, communicationType, commsLimit, commsOffset])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { communications, total, loading, error, refetch: fetch }
}

// =============================================================================
// useCommunication - Single communication
// =============================================================================

interface UseCommunicationResult {
  communication: Communication | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch a single communication
 */
export function useCommunication(
  caseId: string | null,
  communicationId: string | null
): UseCommunicationResult {
  const [communication, setCommunication] = useState<Communication | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId || !communicationId) {
      setCommunication(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await getCommunication(caseId, communicationId)
      setCommunication(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch communication')
      setCommunication(null)
    } finally {
      setLoading(false)
    }
  }, [caseId, communicationId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { communication, loading, error, refetch: fetch }
}

// =============================================================================
// useCommunicationMutations - Create and delete communications
// =============================================================================

interface UseCommunicationMutationsResult {
  creating: boolean
  deleting: boolean
  error: string | null
  sendOutbound: (caseId: string, comm: CreateOutboundRequest) => Promise<Communication | null>
  logInbound: (caseId: string, comm: RecordInboundRequest) => Promise<Communication | null>
  remove: (caseId: string, communicationId: string) => Promise<boolean>
  clearError: () => void
}

/**
 * Hook for communication mutations
 */
export function useCommunicationMutations(): UseCommunicationMutationsResult {
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sendOutbound = useCallback(
    async (caseId: string, comm: CreateOutboundRequest): Promise<Communication | null> => {
      setCreating(true)
      setError(null)

      try {
        const result = await createOutbound(caseId, comm)
        return result
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to send outbound communication')
        return null
      } finally {
        setCreating(false)
      }
    },
    []
  )

  const logInbound = useCallback(
    async (caseId: string, comm: RecordInboundRequest): Promise<Communication | null> => {
      setCreating(true)
      setError(null)

      try {
        const result = await recordInbound(caseId, comm)
        return result
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to record inbound communication')
        return null
      } finally {
        setCreating(false)
      }
    },
    []
  )

  const remove = useCallback(async (caseId: string, communicationId: string): Promise<boolean> => {
    setDeleting(true)
    setError(null)

    try {
      await deleteCommunication(caseId, communicationId)
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete communication')
      return false
    } finally {
      setDeleting(false)
    }
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return { creating, deleting, error, sendOutbound, logInbound, remove, clearError }
}

// =============================================================================
// useTemplates - List communication templates
// =============================================================================

interface UseTemplatesResult {
  templates: CommunicationTemplate[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch communication templates for the organisation
 */
export function useTemplates(params?: ListTemplatesQuery): UseTemplatesResult {
  const [templates, setTemplates] = useState<CommunicationTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Destructure primitive fields from `params` so the dep array below depends on
  // stable scalar values, not on the object reference. Callers commonly pass a
  // fresh object literal (`{ page, perPage }`) every render — including the
  // object itself in the dep array recreates `fetch` on every render, which
  // in turn re-fires the `useEffect([fetch])` below, triggering an infinite
  // fetch loop that exhausts the browser's HTTP socket pool
  // (net::ERR_INSUFFICIENT_RESOURCES).
  const activeOnly = params?.activeOnly
  const templateCommunicationType = params?.communicationType

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Pass `params` through (not a reconstructed literal) to preserve the
      // shape's distinction between "absent" and "explicit undefined" under
      // exactOptionalPropertyTypes. Primitive fields are in the dep array
      // below so the closure captures fresh `params` whenever any field
      // actually changes.
      const data = await listTemplates(params)
      setTemplates(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch templates')
      setTemplates([])
    } finally {
      setLoading(false)
    }
    // Intentionally omits `params` (unstable object reference); we depend on
    // each primitive field instead. See the comment block above the destructuring.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOnly, templateCommunicationType])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { templates, loading, error, refetch: fetch }
}

// =============================================================================
// useTemplate - Single template
// =============================================================================

interface UseTemplateResult {
  template: CommunicationTemplate | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch a single template
 */
export function useTemplate(templateId: string | null): UseTemplateResult {
  const [template, setTemplate] = useState<CommunicationTemplate | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!templateId) {
      setTemplate(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await getTemplate(templateId)
      setTemplate(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch template')
      setTemplate(null)
    } finally {
      setLoading(false)
    }
  }, [templateId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { template, loading, error, refetch: fetch }
}

// =============================================================================
// useTemplateMutations - Create, update, delete templates
// =============================================================================

interface UseTemplateMutationsResult {
  creating: boolean
  updating: boolean
  deleting: boolean
  rendering: boolean
  error: string | null
  create: (input: CreateTemplateRequest) => Promise<CommunicationTemplate | null>
  update: (
    templateId: string,
    input: UpdateTemplateRequest
  ) => Promise<CommunicationTemplate | null>
  remove: (templateId: string) => Promise<boolean>
  render: (templateId: string, request: RenderTemplateRequest) => Promise<RenderedTemplate | null>
  clearError: () => void
}

/**
 * Hook for template mutations
 */
export function useTemplateMutations(): UseTemplateMutationsResult {
  const [creating, setCreating] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [rendering, setRendering] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const create = useCallback(
    (input: CreateTemplateRequest) => runMutation(
      { setLoading: setCreating, setError, errorMessage: 'Failed to create template', fallback: null as CommunicationTemplate | null },
      () => createTemplate(input)
    ), []
  )

  const update = useCallback(
    (templateId: string, input: UpdateTemplateRequest) => runMutation(
      { setLoading: setUpdating, setError, errorMessage: 'Failed to update template', fallback: null as CommunicationTemplate | null },
      () => updateTemplate(templateId, input)
    ), []
  )

  const remove = useCallback(
    (templateId: string) => runMutation(
      { setLoading: setDeleting, setError, errorMessage: 'Failed to delete template', fallback: false },
      async () => { await deleteTemplate(templateId); return true }
    ), []
  )

  const renderFn = useCallback(
    (templateId: string, request: RenderTemplateRequest) => runMutation(
      { setLoading: setRendering, setError, errorMessage: 'Failed to render template', fallback: null as RenderedTemplate | null },
      () => renderTemplate(templateId, request)
    ), []
  )

  const clearError = useCallback(() => { setError(null) }, [])

  return {
    creating, updating, deleting, rendering, error,
    create, update, remove, render: renderFn, clearError,
  }
}
